import { useCallback, useEffect, useMemo, useState } from 'react';
import { baixarCsv } from '../../design/csv';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { diaMes, hojeBrasilia, lerControle, lerFerias } from '../../escala';
import type { ControleDaPessoa, FeriasDaPessoaNaEscala } from '../../escala';
import { Kpi, irParaEscala } from './QuadroDeHorario';

/**
 * CONTROLE (Carlos, 24/10/2026): "controle de faltas, atestados, férias, folgas feriado".
 * Por pessoa, no período: dias trabalhados, domingos, folgas, faltas, atestados, férias, feriados
 * trabalhados × folgas compensadas e o que ainda falta compensar (esse olha a vida toda).
 */

type Periodo = 'ano' | 'mes' | 'mes-passado';

function datasDo(periodo: Periodo, hoje: string): { de: string; ate: string; rotulo: string } {
  const ano = hoje.slice(0, 4);
  const mes = Number(hoje.slice(5, 7));
  const ultimo = (a: number, m: number) => new Date(Date.UTC(a, m, 0)).getUTCDate();
  if (periodo === 'ano')
    return { de: `${ano}-01-01`, ate: `${ano}-12-31`, rotulo: `Ano de ${ano}` };
  const [a, m] =
    periodo === 'mes'
      ? [Number(ano), mes]
      : mes === 1
        ? [Number(ano) - 1, 12]
        : [Number(ano), mes - 1];
  const mm = String(m).padStart(2, '0');
  return {
    de: `${String(a)}-${mm}-01`,
    ate: `${String(a)}-${mm}-${String(ultimo(a, m))}`,
    rotulo: `${mm}/${String(a)}`,
  };
}

type Ordem = 'nome' | 'faltas' | 'atestados' | 'ferias' | 'aPagar' | 'domingos' | 'saldo';

export function AbaControle({ parametros, rota }: { parametros: URLSearchParams; rota: string }) {
  const periodo =
    (['ano', 'mes', 'mes-passado'] as const).find((p) => p === parametros.get('periodo')) ?? 'ano';
  const { de, ate, rotulo } = datasDo(periodo, hojeBrasilia());
  const [dados, setDados] = useState<ControleDaPessoa[] | null>(null);
  const [erro, setErro] = useState(false);
  const [ordem, setOrdem] = useState<Ordem>('nome');
  // o saldo de férias de cada um ("lá no controle tem que aparecer também", dono 24/10)
  const [ferias, setFerias] = useState<Map<string, FeriasDaPessoaNaEscala>>(new Map());
  useEffect(() => {
    void lerFerias().then(
      (r) => r.ok && setFerias(new Map(r.dados.pessoas.map((p) => [p.pessoaId, p]))),
    );
  }, []);
  const saldoDe = (id: string) => ferias.get(id)?.ferias?.saldo ?? null;
  const tirarAte = (id: string) => ferias.get(id)?.ferias?.proximoVencimento?.limite ?? null;

  const carregar = useCallback(() => {
    setErro(false);
    setDados(null);
    void lerControle(de, ate).then((r) => (r.ok ? setDados(r.dados.pessoas) : setErro(true)));
  }, [de, ate]);
  useEffect(carregar, [carregar]);

  const linhas = useMemo(() => {
    const l = [...(dados ?? [])];
    const chave: Record<Ordem, (x: ControleDaPessoa) => number | string> = {
      nome: (x) => x.nome,
      faltas: (x) => -x.faltas,
      atestados: (x) => -x.atestados,
      ferias: (x) => -x.ferias,
      aPagar: (x) => -x.feriadosAPagar.length,
      domingos: (x) => -x.domingosTrabalhados,
      saldo: (x) => -(saldoDe(x.pessoaId) ?? -1),
    };
    const k = chave[ordem];
    return l.sort((a, b) => {
      const va = k(a);
      const vb = k(b);
      return typeof va === 'string'
        ? va.localeCompare(String(vb))
        : va - Number(vb) || a.nome.localeCompare(b.nome);
    });
  }, [dados, ordem, ferias]);

  if (erro) return <ErroDeArea oQue="o controle" aoTentar={carregar} />;

  const soma = (f: (x: ControleDaPessoa) => number) => (dados ?? []).reduce((s, x) => s + f(x), 0);

  const exportar = () =>
    baixarCsv(`controle-escala-${de}-a-${ate}`, [
      [
        'Pessoa',
        'Dias trabalhados',
        'Domingos trabalhados',
        'Folgas',
        'Faltas',
        'Atestados',
        'Férias (dias)',
        'Feriados trabalhados',
        'Folgas de feriado tiradas',
        'Feriados a compensar',
        'Férias disponíveis (dias)',
        'Tirar até',
        'Próximas férias',
      ],
      ...linhas.map((x) => [
        x.nome,
        x.trabalhados,
        x.domingosTrabalhados,
        x.folgas,
        x.faltas,
        x.atestados,
        x.ferias,
        x.feriadosTrabalhados.map(diaMes).join(' '),
        x.compensadas.length,
        x.feriadosAPagar.map(diaMes).join(' '),
        saldoDe(x.pessoaId) ?? 'sem admissão',
        tirarAte(x.pessoaId) ? diaMes(tirarAte(x.pessoaId) ?? '') : '',
        x.proximasFerias ? `${diaMes(x.proximasFerias.de)} a ${diaMes(x.proximasFerias.ate)}` : '',
      ]),
    ]);

  const th = (o: Ordem, texto: string) => (
    <th scope="col">
      <button
        type="button"
        className={`qh-ordenar${ordem === o ? ' qh-ordenar-on' : ''}`}
        onClick={() => setOrdem(o)}
        aria-pressed={ordem === o}
      >
        {texto}
      </button>
    </th>
  );

  return (
    <div className="qh-corpo">
      <div className="qh-barra">
        <div className="qh-segmentos" role="radiogroup" aria-label="Período">
          {(
            [
              ['ano', 'Este ano'],
              ['mes', 'Este mês'],
              ['mes-passado', 'Mês passado'],
            ] as const
          ).map(([id, txt]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={periodo === id}
              className={periodo === id ? 'qh-seg qh-seg-on' : 'qh-seg'}
              onClick={() =>
                irParaEscala(rota, { aba: 'controle', periodo: id === 'ano' ? null : id })
              }
            >
              {txt}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="qh-btn qh-btn-sec"
          onClick={exportar}
          disabled={!dados?.length}
        >
          <Icone nome="down" tamanho={15} />
          Baixar planilha
        </button>
      </div>

      {dados === null ? (
        <div className="qh-carregando">Somando…</div>
      ) : dados.length === 0 ? (
        <EstadoVazio
          icone="gauge"
          titulo="Ninguém no quadro ainda"
          texto="Coloque as pessoas em Configurar."
        />
      ) : (
        <>
          <div className="qh-kpis">
            <Kpi rotulo={`Faltas · ${rotulo}`} valor={soma((x) => x.faltas)} />
            <Kpi rotulo={`Atestados · ${rotulo}`} valor={soma((x) => x.atestados)} />
            <Kpi rotulo={`Dias de férias · ${rotulo}`} valor={soma((x) => x.ferias)} />
            <Kpi
              rotulo="Feriados a compensar (hoje)"
              valor={soma((x) => x.feriadosAPagar.length)}
              destaque={soma((x) => x.feriadosAPagar.length) > 0}
            />
          </div>
          <div className="qh-rolagem">
            <table className="qh-controle">
              <thead>
                <tr>
                  {th('nome', 'Pessoa')}
                  <th scope="col">Trabalhou</th>
                  {th('domingos', 'Domingos')}
                  <th scope="col">Folgas</th>
                  {th('faltas', 'Faltas')}
                  {th('atestados', 'Atestados')}
                  {th('ferias', 'Férias')}
                  <th scope="col">Feriados trabalhados</th>
                  <th scope="col">Folgas de feriado</th>
                  {th('aPagar', 'A compensar')}
                  {th('saldo', 'Férias disponíveis')}
                  <th scope="col">Tirar até</th>
                  <th scope="col">Próximas férias</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((x) => (
                  <tr key={x.pessoaId}>
                    <th scope="row">{x.nome}</th>
                    <td>{x.trabalhados}</td>
                    <td>{x.domingosTrabalhados}</td>
                    <td>{x.folgas}</td>
                    <td className={x.faltas ? 'qh-n-ruim' : 'qh-n-zero'}>{x.faltas}</td>
                    <td className={x.atestados ? 'qh-n-atencao' : 'qh-n-zero'}>{x.atestados}</td>
                    <td className={x.ferias ? '' : 'qh-n-zero'}>{x.ferias}</td>
                    <td className="qh-datas">
                      {x.feriadosTrabalhados.map(diaMes).join(', ') || '—'}
                    </td>
                    <td className="qh-datas">
                      {x.compensadas.length
                        ? x.compensadas
                            .map(
                              (c) =>
                                `${diaMes(c.dia)}${c.feriado ? ` (${diaMes(c.feriado)})` : ''}`,
                            )
                            .join(', ')
                        : '—'}
                    </td>
                    <td className={x.feriadosAPagar.length ? 'qh-n-atencao qh-datas' : 'qh-n-zero'}>
                      {x.feriadosAPagar.length
                        ? `${String(x.feriadosAPagar.length)}: ${x.feriadosAPagar.map(diaMes).join(', ')}`
                        : '0'}
                    </td>
                    <td
                      className={saldoDe(x.pessoaId) ? 'qh-n-forte' : 'qh-n-zero'}
                      title={saldoDe(x.pessoaId) === null ? 'Sem data de admissão' : undefined}
                    >
                      {saldoDe(x.pessoaId) === null ? '—' : `${String(saldoDe(x.pessoaId))} dias`}
                    </td>
                    <td
                      className={
                        tirarAte(x.pessoaId) &&
                        Date.parse(`${tirarAte(x.pessoaId) ?? ''}T12:00:00Z`) - Date.now() <
                          60 * 86_400_000
                          ? 'qh-n-atencao'
                          : ''
                      }
                    >
                      {tirarAte(x.pessoaId)
                        ? diaMes(tirarAte(x.pessoaId) ?? '') +
                          '/' +
                          (tirarAte(x.pessoaId) ?? '').slice(2, 4)
                        : '—'}
                    </td>
                    <td className="qh-datas">
                      {x.proximasFerias
                        ? `${diaMes(x.proximasFerias.de)} a ${diaMes(x.proximasFerias.ate)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="qh-mudo">
            Conta pelo que está no quadro. “A compensar” olha todos os feriados já trabalhados (não
            só os do período) contra as folgas compensadas (FC) marcadas.
          </p>
        </>
      )}
    </div>
  );
}
